import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, StatusTag, type DropdownMenuItem } from '@/components/ui';
import type { Organization } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';
import { getOrgStatusConfig, formatCountVN, orgHealth } from './orgFormat';

export interface OrgRowStats {
    // EAA-1818: was `tenantCount` (OrganizationAppScope rows, which do not
    // nest) — the design's "Đơn vị" column is the CHILD-ORG count
    // (`Organization.parent`), a different backend entity. Renamed, not
    // added alongside, since nothing in this table reads a tenant count any
    // more (the quick-peek drawer still shows real tenants, but it reads its
    // own `tenants` list directly in `OrganizationsPage.tsx`, not this map).
    unitCount: number | undefined;
    accountCount: number | undefined;
}

interface OrganizationsTableProps {
    organizations: Organization[];
    stats: Record<string, OrgRowStats>;
    selectedOrgId: string | null;
    onPeek: (org: Organization) => void;
    onOpenDetail: (org: Organization) => void;
    /**
     * EAA-1891: open the cross-tree move dialog for this row. OPTIONAL, and the
     * row action is omitted when it is absent — the move is super-admin only
     * (D-09), so the page passes this only for a super-admin. Hiding the entry is
     * UX; `POST /organizations/{id}/move` is the real gate.
     */
    onMoveIntoOrg?: (org: Organization) => void;
}

/**
 * EAA-1815: `/organizations` list table — frame 21 lines 327-650.
 * EAA-1818: "Đơn vị" (child-org count) replaced the old tenant-count-based
 * "Cơ sở" column — `/organizations` lists ROOT orgs only, and its per-row
 * health/count now reflects the đơn vị tree (`Organization.parent`), not
 * `OrganizationAppScope` (app scope, a different, non-nesting entity).
 *
 * "Đơn vị" and "Tài khoản" are not on the `Organization` payload itself (the
 * BE list serializer only returns id/code/name/is_active/branding/timestamps
 * — verified against `services/portal-api/portal_api/directory/org_serializers.py`).
 * Both columns come from a per-row secondary fetch the parent page runs via
 * `useQueries` (child-org count for the count + health, assignments for the
 * account total) — real numbers from real, already-integrated endpoints,
 * never a placeholder. Until a row's fetch resolves the cell shows a
 * skeleton dash, never a guessed number.
 */
export function OrganizationsTable({
    organizations,
    stats,
    selectedOrgId,
    onPeek,
    onOpenDetail,
    onMoveIntoOrg,
}: OrganizationsTableProps) {
    const { t } = useTranslation();
    const statusConfig = useMemo(() => getOrgStatusConfig(t), [t]);

    return (
        <div className="portal-table-scroll">
            <table className="portal-admin-table portal-orgs-table">
                <thead>
                    <tr>
                        <th>{t('admin.organizations.orgWord')}</th>
                        <th>{t('admin.organizations.columnCode')}</th>
                        <th>{t('admin.organizations.columnUnitCount')}</th>
                        <th>{t('admin.organizations.columnAccountCount')}</th>
                        <th>{t('admin.organizations.columnStatus')}</th>
                        <th>
                            <span className="portal-sr-only">{t('common.actions')}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {organizations.map((org) => {
                        const rowStats = stats[org.id] ?? {
                            unitCount: undefined,
                            accountCount: undefined,
                        };
                        const selected = org.id === selectedOrgId;
                        // EAA-1818: `orgHealth`'s third arg picks which "empty" state to
                        // return — this list's row pill means "no child đơn vị", which is
                        // a distinct state from `OrgDetailHeader`'s "no cơ sở/tenant" (that
                        // page keeps calling `orgHealth` with its default, unchanged).
                        const health = orgHealth(org.is_active, rowStats.unitCount, 'no_unit');
                        const muted = health === 'inactive';
                        const avatarTone = selected ? 'accent' : muted ? 'muted' : 'neutral';

                        const rowActions: DropdownMenuItem[] = [
                            {
                                key: 'peek',
                                label: t('admin.organizations.actionPeek'),
                                onSelect: () => onPeek(org),
                            },
                            {
                                key: 'open',
                                label: t('admin.organizations.openOrgPage'),
                                onSelect: () => onOpenDetail(org),
                            },
                        ];
                        // EAA-1891: the hierarchy action goes last, after the two
                        // read actions — it restructures two trees at once, so it
                        // must not sit where a mis-click on "Mở trang tổ chức"
                        // lands.
                        if (onMoveIntoOrg) {
                            rowActions.push({
                                key: 'move',
                                label: t('admin.organizations.move.action'),
                                onSelect: () => onMoveIntoOrg(org),
                            });
                        }

                        return (
                            <tr
                                key={org.id}
                                className={
                                    (selected ? 'is-selected ' : '') + (muted ? 'is-muted' : '')
                                }
                                onClick={() => onPeek(org)}
                                style={{ cursor: 'pointer' }}
                            >
                                <td>
                                    <span className="portal-orgs-org-cell">
                                        <OrgAvatar name={org.name} size={30} tone={avatarTone} />
                                        <span className="portal-orgs-org-name">{org.name}</span>
                                    </span>
                                </td>
                                <td>
                                    <code>{org.code}</code>
                                </td>
                                <td>
                                    {rowStats.unitCount === undefined
                                        ? '…'
                                        : formatCountVN(rowStats.unitCount)}
                                </td>
                                <td>
                                    {rowStats.accountCount === undefined
                                        ? '…'
                                        : formatCountVN(rowStats.accountCount)}
                                </td>
                                <td>
                                    <StatusTag status={health} config={statusConfig} />
                                </td>
                                <td onClick={(event) => event.stopPropagation()}>
                                    <DropdownMenu
                                        items={rowActions}
                                        triggerAriaLabel={t(
                                            'admin.organizations.rowActionsAriaLabel',
                                            { name: org.name }
                                        )}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default OrganizationsTable;
